import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import KPICard from './components/KPICard';
import FleetMap from './components/FleetMap';
import DriverLeaderboard from './components/DriverLeaderboard';
import PerformanceChart from './components/PerformanceChart';
import MaintenanceCalendar from './components/MaintenanceCalendar';
import VehicleUtilizationHeatmap from './components/VehicleUtilizationHeatmap';
import FilterPanel from './components/FilterPanel';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const FleetPerformanceDashboard = () => {
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [isAutoRefresh, setIsAutoRefresh] = useState(true);
  const [selectedFilters, setSelectedFilters] = useState({});
  const [comparisonMode, setComparisonMode] = useState(false);

  // Mock KPI data
  const kpiData = [
    {
      title: 'Fuel Efficiency',
      value: '8.2',
      unit: 'MPG',
      change: 5.2,
      changeType: 'positive',
      icon: 'Fuel',
      color: 'success'
    },
    {
      title: 'On-Time Delivery',
      value: '94.8',
      unit: '%',
      change: 2.1,
      changeType: 'positive',
      icon: 'Clock',
      color: 'primary'
    },
    {
      title: 'Driver Safety Score',
      value: '92.5',
      unit: '/100',
      change: -1.3,
      changeType: 'negative',
      icon: 'Shield',
      color: 'warning'
    },
    {
      title: 'Maintenance Compliance',
      value: '87.2',
      unit: '%',
      change: 3.7,
      changeType: 'positive',
      icon: 'Wrench',
      color: 'accent'
    }
  ];

  // Auto-refresh functionality
  useEffect(() => {
    let interval;
    if (isAutoRefresh) {
      interval = setInterval(() => {
        setLastUpdate(new Date());
      }, 120000); // 2 minutes
    }
    return () => clearInterval(interval);
  }, [isAutoRefresh]);

  const handleFiltersChange = (filters) => {
    setSelectedFilters(filters);
    // In a real app, this would trigger data refetch
    console.log('Filters changed:', filters);
  };

  const handleDateRangeChange = (dateRange) => {
    // In a real app, this would update the date range for all components
    console.log('Date range changed:', dateRange);
  };

  const handleRefresh = () => {
    setLastUpdate(new Date());
    // In a real app, this would trigger data refetch for all components
  };

  const formatLastUpdate = () => {
    const now = new Date();
    const diff = Math.floor((now - lastUpdate) / 1000);
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    return `${Math.floor(diff / 3600)}h ago`;
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Page Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Fleet Performance Dashboard</h1>
              <p className="text-muted-foreground">
                Comprehensive vehicle tracking, driver analytics, and route optimization insights for data-driven logistics decisions.
              </p>
            </div>
            
            <div className="flex items-center space-x-3 mt-4 lg:mt-0">
              <div className="flex items-center space-x-2 px-3 py-2 bg-muted rounded-lg">
                <div className="status-indicator status-online"></div>
                <span className="text-sm text-muted-foreground">Live • {formatLastUpdate()}</span>
              </div>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setComparisonMode(!comparisonMode)}
                iconName="BarChart3"
                iconSize={16}
              >
                {comparisonMode ? 'Exit Compare' : 'Compare'}
              </Button>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsAutoRefresh(!isAutoRefresh)}
                iconName={isAutoRefresh ? "Pause" : "Play"}
                iconSize={16}
              >
                {isAutoRefresh ? 'Pause' : 'Resume'}
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                iconName="RefreshCw"
                iconSize={16}
              >
                Refresh
              </Button>
            </div>
          </div>

          {/* Filters Panel */}
          <div className="mb-8">
            <FilterPanel 
              onFiltersChange={handleFiltersChange}
              onDateRangeChange={handleDateRangeChange}
            />
          </div>

          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {kpiData?.map((kpi, index) => (
              <KPICard
                key={index}
                title={kpi?.title}
                value={kpi?.value}
                unit={kpi?.unit}
                change={kpi?.change}
                changeType={kpi?.changeType}
                icon={kpi?.icon}
                color={kpi?.color}
              />
            ))}
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-8">
            {/* Fleet Map - Takes 3 columns on large screens */}
            <div className="lg:col-span-3">
              <FleetMap />
            </div>
            
            {/* Driver Leaderboard - Takes 1 column */}
            <div className="lg:col-span-1">
              <DriverLeaderboard />
            </div>
          </div>

          {/* Performance Chart - Full Width */}
          <div className="mb-8">
            <PerformanceChart />
          </div>

          {/* Secondary Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Maintenance Calendar */}
            <div>
              <MaintenanceCalendar />
            </div>
            
            {/* Vehicle Utilization Heatmap */}
            <div>
              <VehicleUtilizationHeatmap />
            </div>
          </div>

          {/* Mobile Optimization Notice */}
          <div className="mt-8 p-4 bg-accent/10 border border-accent/20 rounded-lg lg:hidden">
            <div className="flex items-start space-x-3">
              <Icon name="Smartphone" size={20} color="var(--color-accent)" />
              <div>
                <h4 className="font-medium text-foreground mb-1">Mobile Optimized</h4>
                <p className="text-sm text-muted-foreground">
                  This dashboard is optimized for mobile viewing. Swipe through charts and tap to expand sections for detailed analysis.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default FleetPerformanceDashboard;